import axios from "axios";
import sanitizeHtml from "sanitize-html";
import "./PastProcedures.css";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Loader from "../Loader";
import {
  buildDiagnosesSection,
  buildPastProcedures,
  buildUnauthorizedHtmlResponse,
} from "./html-builders";
import { DIAGNOSES_FORM } from "./forms/diagnoses";
import { DOI_FIELDS, DOI_FORM } from "./forms/date-of-injury";

const BASE_URI =
  process.env.NODE_ENV === "production"
    ? "https://b364hjicigdtxzbjrpdiowt4gy0zrmhi.lambda-url.us-west-2.on.aws"
    : "https://i5ts4zvkk7p3f3gre4cs5yvtzy0qltww.lambda-url.us-west-2.on.aws";

const AUTH_LINK =
  "https://drchrono.com/o/authorize/?redirect_uri=https%3A%2F%2Fhgg7yccm6rpp5vy4yrr7cbfioi0yvdrd.lambda-url.us-west-2.on.aws%2F&response_type=code&client_id=l5vFllbXnjtYzj46TO8M1It6RtC9HkqG8JC4bYWc";

const COPY = "Copy to Clipboard";
const COPIED = "Copied!";

export default function PastProcedures() {
  const [activeTab, setActiveTab] = useState<"procedures" | "onboarding">(
    "procedures"
  );
  let [loading, setLoading] = useState(true);
  const [appts, setAppts] = useState<any>("");
  const [searchParams] = useSearchParams();
  const [copyButtonText, setCopyButtonText] = useState<any>(COPY);
  const [onboardingCopied, setOnboardingCopied] = useState(false);
  const [error, setError] = useState<any>("");

  const getAppts = (
    searchParams: URLSearchParams,
    opts?: { clearCache?: boolean }
  ) => {
    setLoading(true);
    axios
      .get(
        `${BASE_URI}?jwt=${searchParams.get("jwt")}${
          !!opts?.clearCache ? "&clearCache=true" : ""
        }`
      )
      .then((data) => {
        setAppts(data.data);
        setLoading(false);
      })
      .catch(() => {
        setError(buildUnauthorizedHtmlResponse());
        setLoading(false);
      });
  };

  useEffect(() => {
    if (activeTab === "procedures") {
      getAppts(searchParams);
    }
  }, [activeTab, searchParams]);

  const [openSections, setOpenSections] = useState<Record<string, boolean>>({});

  // Toggle function to open or close a specific section
  const toggleSection = (index: string) => {
    setOpenSections((prev) => ({
      ...prev,
      [index]: !prev[index], // Toggle the visibility of the clicked section
    }));
  };

  const copyAsHTML = () => {
    const content = document.getElementById("content");
    if (content) {
      copy();
      /*
      const raw = content.innerHTML;
      const clean = sanitizeHtml(raw, {
        allowedTags: ["div", "br", "b"],
        allowedAttributes: { "*": ["style"] },
      });
      navigator.clipboard.writeText(clean).then(() => {
        alert("Copied");
      });
       */
    }
  };

  const copy = () => {
    const input = document.createElement("textarea");
    const toIgnore = ["diagnoses", "doi"];
    const text =
      "<div>" +
      Array.from(document.querySelectorAll("div.row"))
        .filter((e: any) => !toIgnore.includes(e.id))
        .map((e: any) =>
          sanitizeHtml(e.innerHTML, {
            allowedTags: ["b", "br"],
            allowedAttributes: { "*": ["style"] },
          })
        )
        .join("\n\n") +
      "</div>";

    document.body.appendChild(input);
    input.value = text;
    input.select();
    document.execCommand("copy");
    document.body.removeChild(input);
    setCopyButtonText(COPIED);
    setTimeout(() => setCopyButtonText(COPY), 5000);

    /*
     * Not working
    navigator.clipboard.writeText(text).then(() => {
      setCopyButtonText(COPIED);
      setTimeout(() => setCopyButtonText(COPY), 5000);
    });
     */
  };

  const tabs = [
    { id: "procedures" as const, label: "Prior Procedure List" },
    { id: "onboarding" as const, label: "New Provider Onboarding" },
  ];

  if (error)
    return <div id="error" dangerouslySetInnerHTML={{ __html: error }}></div>;

  return (
    <div className="PatientProfile">
      <div className="tab-bar">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={`tab-button ${activeTab === tab.id ? "active" : ""}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>
      <div className="title">
        <h2>{tabs.find((t) => t.id === activeTab)?.label}</h2>
      </div>

      {activeTab === "procedures" ? (
        <div>
          <div className="tab-actions">
            {appts?.data?.metadata?.lastUpdated?.epoch && (
              <b>
                Last Updated:{" "}
                {new Date(
                  appts?.data?.metadata?.lastUpdated?.epoch
                ).toLocaleString()}{" "}
              </b>
            )}
            <button
              className="refresh-button"
              disabled={loading}
              onClick={() => getAppts(searchParams, { clearCache: true })}
            >
              {loading ? <span className="refresh-icon"></span> : <></>}
              Refresh
            </button>
            <button
              className="refresh-button"
              disabled={loading || copyButtonText === COPIED}
              onClick={() => copyAsHTML()}
            >
              {copyButtonText}
            </button>
          </div>
          <div id="content">
          {loading ? (
            <div>
              <Loader />
            </div>
          ) : (
            <>
              {appts.data?.persistentForms[DOI_FORM]?.fields?.[
                DOI_FIELDS.DOI
              ] && (
                <>
                  <div className="data-table">
                    <div className="row" id="doi">
                      <span className="label">
                        <b>DOI: </b>
                      </span>
                      <span className="value">
                        <b>
                          {
                            appts.data.persistentForms[DOI_FORM]?.fields?.[
                              DOI_FIELDS.DOI
                            ].value
                          }
                        </b>
                      </span>
                    </div>
                  </div>
                </>
              )}
              {appts.data.persistentForms[DIAGNOSES_FORM] && (
                <>
                  <div className="data-table">
                    <div className="row" id="diagnoses">
                      <div
                        className="label collapsible"
                        onClick={() => toggleSection("diagnoses")}
                      >
                        <b className="toggle">
                          {openSections["diagnoses"]
                            ? "Diagnoses: "
                            : "Diagnoses: "}
                        </b>
                      </div>
                      <div className="value collapsible">
                        {openSections["diagnoses"] ? (
                          <ul
                            onClick={() => toggleSection("diagnoses")}
                            className="toggle"
                            style={{ margin: "0px", padding: "0px 10px" }}
                          >
                            {buildDiagnosesSection(
                              appts.data.persistentForms[DIAGNOSES_FORM]
                            )}
                          </ul>
                        ) : (
                          <div
                            className="toggle"
                            style={{ textDecoration: "underline" }}
                            onClick={() => toggleSection("diagnoses")}
                          >
                            Show
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </>
              )}
              <div>{buildPastProcedures(appts.data)}</div>
            </>
          )}
        </div>
        </div>
      ) : (
        <div id="content">
          <p>Copy this link and share with unauthorized providers:</p>
          <p>
            <span className="auth-link">{AUTH_LINK}</span>
          </p>
          <button
            className="copy-onboarding-button"
            onClick={() => {
              navigator.clipboard.writeText(AUTH_LINK);
              setOnboardingCopied(true);
              setTimeout(() => setOnboardingCopied(false), 2000);
            }}
          >
            {onboardingCopied ? (
              <>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
                Copied!
              </>
            ) : (
              "Copy Link"
            )}
          </button>
          <p>
            Navigating to this page while signed in to drchrono will authorize
            their account.
          </p>
        </div>
      )}
    </div>
  );
}
